import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import type { SystemSettingsTyped, UpdateTenantAiCreditSettingsRequest } from '../../types';
import { formatCurrency, formatInteger } from '../../utils/formatters';
import { IncidentAiChatPolicySection } from './IncidentAiChatPolicySection';

const DEFAULT_BASE_TOKENS_PER_CREDIT = 1000;
const TIER_KEYS = ['Trial', 'Starter', 'Professional', 'Enterprise'] as const;

type TierKey = typeof TIER_KEYS[number];

interface TenantOverrideDraft {
  monthlyCostCapUsd: string;
  monthlyCreditsEntitlementOverride: string;
  monthlyCreditsOperatingLimit: string;
  monthlyProviderCostCapUsdOverride: string;
  monthlyProviderCostCapOperatingLimitUsd: string;
  overrideReason: string;
  expectedQuotaUpdatedAtUtc: string;
}

function getErrorMessage(error: unknown): string {
  if (!error) {
    return 'Unknown error';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Failed to load AI credits configuration.';
}

function toNullableInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);
  if (Number.isNaN(parsed) || parsed < 0) {
    throw new Error('Value must be a whole number greater than or equal to 0.');
  }

  return parsed;
}

function toNullableDecimal(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number.parseFloat(trimmed);
  if (Number.isNaN(parsed) || parsed < 0) {
    throw new Error('Value must be a number greater than or equal to 0.');
  }

  return parsed;
}

function formatUtcTimestamp(value: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return `${date.toISOString().replace('T', ' ').replace('.000Z', 'Z')} UTC`;
}

export function AiCreditsTab() {
  const queryClient = useQueryClient();
  const [tierInputs, setTierInputs] = useState({
    trial: '50',
    starter: '50',
    professional: '100',
    enterprise: '250',
  });
  const [policyDraft, setPolicyDraft] = useState({
    globalMonthlyProviderCostCapUsd: '',
    globalDailyProviderCostCapUsd: '',
    emergencyStop: false,
    defaultPricePerCreditUsd: '0.03',
    tierDefaults: {} as Record<TierKey, string>,
    tierMaximums: {} as Record<TierKey, string>,
    tierProviderCostCapsUsd: {} as Record<TierKey, string>,
  });
  const [tenantDrafts, setTenantDrafts] = useState<Record<string, TenantOverrideDraft>>({});
  const [tenantErrors, setTenantErrors] = useState<Record<string, string>>({});

  const aiCreditsQuery = useQuery({
    queryKey: ['system-ai-credits', 30],
    queryFn: () => apiClient.getSystemAiCreditsOverview(30),
  });
  const systemSettingsQuery = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => apiClient.getSystemSettings(),
  });
  const aiBudgetStatusQuery = useQuery({
    queryKey: ['system-ai-budget-status'],
    queryFn: () => apiClient.getAiBudgetStatus(),
    refetchInterval: 15000,
  });

  useEffect(() => {
    const settings = systemSettingsQuery.data;
    if (!settings) {
      return;
    }

    setTierInputs({
      trial: String(settings.incidentChatTierCeilings?.trial ?? 50),
      starter: String(settings.incidentChatTierCeilings?.starter ?? 50),
      professional: String(settings.incidentChatTierCeilings?.professional ?? 100),
      enterprise: String(settings.incidentChatTierCeilings?.enterprise ?? 250),
    });

    setPolicyDraft({
      globalMonthlyProviderCostCapUsd: settings.aiCredits.globalMonthlyProviderCostCapUsd?.toString() ?? '',
      globalDailyProviderCostCapUsd: settings.aiCredits.globalDailyProviderCostCapUsd?.toString() ?? '',
      emergencyStop: settings.aiCredits.emergencyStop,
      defaultPricePerCreditUsd: settings.aiCredits.defaultPricePerCreditUsd.toString(),
      tierDefaults: {
        Trial: String(settings.aiCredits.tierDefaults.Trial ?? 50),
        Starter: String(settings.aiCredits.tierDefaults.Starter ?? 200),
        Professional: String(settings.aiCredits.tierDefaults.Professional ?? 2000),
        Enterprise: String(settings.aiCredits.tierDefaults.Enterprise ?? 20000),
      },
      tierMaximums: {
        Trial: String(settings.aiCredits.tierMaximums.Trial ?? 50),
        Starter: String(settings.aiCredits.tierMaximums.Starter ?? 200),
        Professional: String(settings.aiCredits.tierMaximums.Professional ?? 2000),
        Enterprise: String(settings.aiCredits.tierMaximums.Enterprise ?? 20000),
      },
      tierProviderCostCapsUsd: {
        Trial: String(settings.aiCredits.tierProviderCostCapsUsd.Trial ?? 1),
        Starter: String(settings.aiCredits.tierProviderCostCapsUsd.Starter ?? 5),
        Professional: String(settings.aiCredits.tierProviderCostCapsUsd.Professional ?? 20),
        Enterprise: String(settings.aiCredits.tierProviderCostCapsUsd.Enterprise ?? 50),
      },
    });
  }, [systemSettingsQuery.data]);

  useEffect(() => {
    const breakdown = aiCreditsQuery.data?.tenantBreakdown ?? [];
    setTenantDrafts((current) => {
      const next: Record<string, TenantOverrideDraft> = {};
      for (const tenant of breakdown) {
        next[tenant.tenantId] = {
          monthlyCostCapUsd: tenant.monthlyCostCapUsd?.toString() ?? '',
          monthlyCreditsEntitlementOverride: tenant.monthlyCreditsEntitlementOverride?.toString() ?? '',
          monthlyCreditsOperatingLimit: tenant.monthlyCreditsOperatingLimit?.toString() ?? '',
          monthlyProviderCostCapUsdOverride: tenant.monthlyProviderCostCapUsdOverride?.toString() ?? '',
          monthlyProviderCostCapOperatingLimitUsd: tenant.monthlyProviderCostCapOperatingLimitUsd?.toString() ?? '',
          overrideReason: current[tenant.tenantId]?.overrideReason ?? '',
          expectedQuotaUpdatedAtUtc: tenant.quotaUpdatedAtUtc ?? '',
        };
      }

      return next;
    });
  }, [aiCreditsQuery.data?.tenantBreakdown]);

  const updateTenantSettingMutation = useMutation({
    mutationFn: async (args: { tenantId: string; request: UpdateTenantAiCreditSettingsRequest }) =>
      apiClient.updateTenantAiCreditSettings(args.tenantId, args.request),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['system-ai-credits', 30] }),
        queryClient.invalidateQueries({ queryKey: ['system-settings'] }),
      ]);
    },
  });

  const saveTierCeilingsMutation = useMutation({
    mutationFn: async (payload: { trial: number; starter: number; professional: number; enterprise: number }) => {
      const current = systemSettingsQuery.data;
      if (!current) {
        throw new Error('System settings are not loaded.');
      }

      const request: SystemSettingsTyped = {
        ...current,
        incidentChatTierCeilings: payload,
      };

      return apiClient.updateSystemSettings(request);
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData(['system-settings'], updated);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['system-settings'] }),
        queryClient.invalidateQueries({ queryKey: ['incident-ai-chat-platform-policy'] }),
        queryClient.invalidateQueries({ queryKey: ['system-ai-credits', 30] }),
      ]);
    },
  });

  const savePolicyMutation = useMutation({
    mutationFn: async () => {
      const current = systemSettingsQuery.data;
      if (!current) {
        throw new Error('System settings are not loaded.');
      }

      const tierDefaults: Record<string, number> = {};
      const tierMaximums: Record<string, number> = {};
      const tierProviderCostCapsUsd: Record<string, number> = {};

      for (const tier of TIER_KEYS) {
        tierDefaults[tier] = toNullableInt(policyDraft.tierDefaults[tier] ?? '') ?? 0;
        tierMaximums[tier] = toNullableInt(policyDraft.tierMaximums[tier] ?? '') ?? 0;
        tierProviderCostCapsUsd[tier] = toNullableDecimal(policyDraft.tierProviderCostCapsUsd[tier] ?? '') ?? 0;
      }

      const request: SystemSettingsTyped = {
        ...current,
        aiCredits: {
          globalMonthlyProviderCostCapUsd: toNullableDecimal(policyDraft.globalMonthlyProviderCostCapUsd),
          globalDailyProviderCostCapUsd: toNullableDecimal(policyDraft.globalDailyProviderCostCapUsd),
          emergencyStop: policyDraft.emergencyStop,
          defaultPricePerCreditUsd: toNullableDecimal(policyDraft.defaultPricePerCreditUsd) ?? 0,
          tierDefaults,
          tierMaximums,
          tierProviderCostCapsUsd,
        },
      };

      return apiClient.updateSystemSettings(request);
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData(['system-settings'], updated);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['system-settings'] }),
        queryClient.invalidateQueries({ queryKey: ['system-ai-credits', 30] }),
      ]);
    },
  });

  const parsedTierValues = {
    trial: Number.parseInt(tierInputs.trial, 10),
    starter: Number.parseInt(tierInputs.starter, 10),
    professional: Number.parseInt(tierInputs.professional, 10),
    enterprise: Number.parseInt(tierInputs.enterprise, 10),
  };

  const isTierInputValid = Object.values(parsedTierValues).every((value) => !Number.isNaN(value) && value >= 0);
  const configuredPricePerCredit = useMemo(
    () => systemSettingsQuery.data?.aiCredits?.defaultPricePerCreditUsd ?? 0.03,
    [systemSettingsQuery.data?.aiCredits?.defaultPricePerCreditUsd],
  );

  const updateTenantDraft = (tenantId: string, update: Partial<TenantOverrideDraft>) => {
    setTenantDrafts((current) => ({
      ...current,
      [tenantId]: {
        ...current[tenantId],
        ...update,
      },
    }));
  };

  const saveTenantCostCap = (tenantId: string) => {
    const draft = tenantDrafts[tenantId];
    if (!draft) {
      return;
    }

    try {
      const monthlyCostCapUsd = toNullableDecimal(draft.monthlyCostCapUsd);
      setTenantErrors((current) => ({ ...current, [tenantId]: '' }));
      updateTenantSettingMutation.mutate({
        tenantId,
        request: {
          monthlyCostCapUsd,
          expectedQuotaUpdatedAtUtc: draft.expectedQuotaUpdatedAtUtc || undefined,
        },
      });
    } catch (error) {
      setTenantErrors((current) => ({ ...current, [tenantId]: getErrorMessage(error) }));
    }
  };

  const saveTenantOverrides = (tenantId: string, reset: boolean) => {
    const draft = tenantDrafts[tenantId];
    if (!draft) {
      return;
    }

    if (!draft.overrideReason.trim()) {
      setTenantErrors((current) => ({ ...current, [tenantId]: 'Override reason is required.' }));
      return;
    }

    try {
      const request: UpdateTenantAiCreditSettingsRequest = {
        monthlyCreditsEntitlementOverride: reset ? null : toNullableInt(draft.monthlyCreditsEntitlementOverride),
        monthlyCreditsOperatingLimit: reset ? null : toNullableInt(draft.monthlyCreditsOperatingLimit),
        monthlyProviderCostCapUsdOverride: reset ? null : toNullableDecimal(draft.monthlyProviderCostCapUsdOverride),
        monthlyProviderCostCapOperatingLimitUsd: reset ? null : toNullableDecimal(draft.monthlyProviderCostCapOperatingLimitUsd),
        overrideReason: draft.overrideReason.trim(),
        expectedQuotaUpdatedAtUtc: draft.expectedQuotaUpdatedAtUtc || undefined,
      };

      setTenantErrors((current) => ({ ...current, [tenantId]: '' }));
      updateTenantSettingMutation.mutate({ tenantId, request });
    } catch (error) {
      setTenantErrors((current) => ({ ...current, [tenantId]: getErrorMessage(error) }));
    }
  };

  return (
    <div className="space-y-6 min-w-0 max-w-full overflow-x-hidden">
      <section className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/40">
        <h3 className="text-base font-semibold text-blue-900 dark:text-blue-100">AI Credits Configuration</h3>
        <p className="mt-2 text-sm text-blue-800 dark:text-blue-200">
          Configure platform AI policy (global caps, tier shared credits/defaults/maxima, provider caps), then manage tenant overrides with reasoned audit.
        </p>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Platform AI policy controls</h3>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="text-sm">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Global monthly provider cap (USD)</span>
            <input type="number" min={0} step="0.01" value={policyDraft.globalMonthlyProviderCostCapUsd} onChange={(e) => setPolicyDraft((c) => ({ ...c, globalMonthlyProviderCostCapUsd: e.target.value }))} className="mt-1 w-full rounded border border-gray-300 px-2 py-1 dark:border-gray-600 dark:bg-gray-900" />
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Global daily provider cap (USD)</span>
            <input type="number" min={0} step="0.01" value={policyDraft.globalDailyProviderCostCapUsd} onChange={(e) => setPolicyDraft((c) => ({ ...c, globalDailyProviderCostCapUsd: e.target.value }))} className="mt-1 w-full rounded border border-gray-300 px-2 py-1 dark:border-gray-600 dark:bg-gray-900" />
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Default price per credit (USD)</span>
            <input type="number" min={0} step="0.0001" value={policyDraft.defaultPricePerCreditUsd} onChange={(e) => setPolicyDraft((c) => ({ ...c, defaultPricePerCreditUsd: e.target.value }))} className="mt-1 w-full rounded border border-gray-300 px-2 py-1 dark:border-gray-600 dark:bg-gray-900" />
          </label>
          <label className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm dark:border-gray-700">
            <input type="checkbox" checked={policyDraft.emergencyStop} onChange={(e) => setPolicyDraft((c) => ({ ...c, emergencyStop: e.target.checked }))} />
            <span className="font-medium text-gray-700 dark:text-gray-200">Emergency stop</span>
          </label>
        </div>

        <div className="mt-4 min-w-0 overflow-x-auto">
          <table className="min-w-[560px] w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-2 font-semibold">Tier</th>
                <th className="px-3 py-2 font-semibold">Default credits</th>
                <th className="px-3 py-2 font-semibold">Maximum credits</th>
                <th className="px-3 py-2 font-semibold">Provider cap (USD)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {TIER_KEYS.map((tier) => (
                <tr key={tier}>
                  <td className="px-3 py-2">{tier}</td>
                  <td className="px-3 py-2">
                    <input aria-label={`${tier} tier default credits`} type="number" min={0} step={1} value={policyDraft.tierDefaults[tier] ?? ''} onChange={(e) => setPolicyDraft((c) => ({ ...c, tierDefaults: { ...c.tierDefaults, [tier]: e.target.value } }))} className="w-28 rounded border border-gray-300 px-2 py-1 dark:border-gray-600 dark:bg-gray-900" />
                  </td>
                  <td className="px-3 py-2">
                    <input aria-label={`${tier} tier maximum credits`} type="number" min={0} step={1} value={policyDraft.tierMaximums[tier] ?? ''} onChange={(e) => setPolicyDraft((c) => ({ ...c, tierMaximums: { ...c.tierMaximums, [tier]: e.target.value } }))} className="w-28 rounded border border-gray-300 px-2 py-1 dark:border-gray-600 dark:bg-gray-900" />
                  </td>
                  <td className="px-3 py-2">
                    <input aria-label={`${tier} tier provider cap usd`} type="number" min={0} step="0.01" value={policyDraft.tierProviderCostCapsUsd[tier] ?? ''} onChange={(e) => setPolicyDraft((c) => ({ ...c, tierProviderCostCapsUsd: { ...c.tierProviderCostCapsUsd, [tier]: e.target.value } }))} className="w-28 rounded border border-gray-300 px-2 py-1 dark:border-gray-600 dark:bg-gray-900" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button type="button" className="rounded-md bg-primary-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-800 disabled:opacity-60" disabled={savePolicyMutation.isPending} onClick={() => savePolicyMutation.mutate()}>
            {savePolicyMutation.isPending ? 'Saving…' : 'Save AI policy controls'}
          </button>
          {savePolicyMutation.isError ? <p className="text-xs text-red-600 dark:text-red-400">{getErrorMessage(savePolicyMutation.error)}</p> : null}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Incident chat tier ceilings</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Trial = 50, Starter = 50, Professional = 100, Enterprise = 250 by default.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
          {([
            ['trial', 'Trial'],
            ['starter', 'Starter'],
            ['professional', 'Professional'],
            ['enterprise', 'Enterprise'],
          ] as const).map(([key, label]) => (
            <label key={key} className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span>
              <input type="number" min={0} step={1} value={tierInputs[key]} onChange={(event) => setTierInputs((current) => ({ ...current, [key]: event.target.value }))} className="mt-2 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100" disabled={saveTierCeilingsMutation.isPending} />
            </label>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button type="button" className="rounded-md bg-primary-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60" disabled={!isTierInputValid || saveTierCeilingsMutation.isPending} onClick={() => saveTierCeilingsMutation.mutate(parsedTierValues)}>
            {saveTierCeilingsMutation.isPending ? 'Saving…' : 'Save tier ceilings'}
          </button>
          {saveTierCeilingsMutation.isError ? <p className="text-xs text-red-600 dark:text-red-400">{getErrorMessage(saveTierCeilingsMutation.error)}</p> : null}
        </div>
      </section>

      <IncidentAiChatPolicySection />

      {aiCreditsQuery.isError && (
        <section className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/40">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to load AI credits overview.</p>
          <p className="mt-1 text-xs text-red-700 dark:text-red-300">{getErrorMessage(aiCreditsQuery.error)}</p>
        </section>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Current Credit Configuration</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Price Per Credit</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(configuredPricePerCredit)}</p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Base Tokens Per Credit</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{formatInteger(DEFAULT_BASE_TOKENS_PER_CREDIT)}</p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Actual Azure Cost (30 days)</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(aiCreditsQuery.data?.totalActualCost ?? 0)}</p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Customer Billed Cost (30 days)</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(aiCreditsQuery.data?.totalEstimatedCost ?? 0)}</p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Estimated Provider Cost (30 days)</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(aiCreditsQuery.data?.totalActualCost ?? 0)}</p>
          </div>
        </div>
      </section>

      {aiBudgetStatusQuery.isError ? (
        <section className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/40" role="alert">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to load AI budget reservation status.</p>
          <p className="mt-1 text-xs text-red-700 dark:text-red-300">{getErrorMessage(aiBudgetStatusQuery.error)}</p>
        </section>
      ) : null}

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Provider reservation status</h3>
          <button type="button" className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800" onClick={() => void aiBudgetStatusQuery.refetch()} disabled={aiBudgetStatusQuery.isFetching}>
            {aiBudgetStatusQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Unsettled Attempts</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatInteger(aiBudgetStatusQuery.data?.unsettledAttempts ?? 0)}</p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Expired Unsettled Attempts</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatInteger(aiBudgetStatusQuery.data?.expiredUnsettledAttempts ?? 0)}</p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Internal Provider Cost</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(aiBudgetStatusQuery.data?.internalProviderCostUsd ?? 0)}</p>
          </div>
        </div>
        <div className="mt-4 min-w-0 overflow-x-auto">
          <table className="min-w-[520px] w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr><th className="px-3 py-2 font-semibold">Period</th><th className="px-3 py-2 font-semibold">Used</th><th className="px-3 py-2 font-semibold">Reserved</th><th className="px-3 py-2 font-semibold">Accounting</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {(aiBudgetStatusQuery.data?.periods ?? []).map((period) => (
                <tr key={`${period.isDaily ? 'daily' : 'monthly'}-${period.startUtc}`}>
                  <td className="px-3 py-2">{period.isDaily ? 'Daily' : 'Monthly'} · {formatUtcTimestamp(period.startUtc)}</td>
                  <td className="px-3 py-2">{formatCurrency(period.usedUsd)}</td>
                  <td className="px-3 py-2">{formatCurrency(period.reservedUsd)}</td>
                  <td className="px-3 py-2">{period.accountingBlocked ? 'Blocked' : 'Healthy'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tenant entitlement override controls</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">System admin only. Include a reason for durable audit logging. Leave fields empty to inherit/reset.</p>
        <div className="mt-4 min-w-0 overflow-x-auto">
          <table className="min-w-[1180px] w-full text-left text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-2 py-2 font-semibold">Tenant</th>
                <th className="px-2 py-2 font-semibold">Customer Cap (USD)</th>
                <th className="px-2 py-2 font-semibold">Entitlement Credits</th>
                <th className="px-2 py-2 font-semibold">Operating Credits</th>
                <th className="px-2 py-2 font-semibold">Entitlement Provider Cap</th>
                <th className="px-2 py-2 font-semibold">Operating Provider Cap</th>
                <th className="px-2 py-2 font-semibold">Reason</th>
                <th className="px-2 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {(aiCreditsQuery.data?.tenantBreakdown ?? []).map((tenant) => {
                const draft = tenantDrafts[tenant.tenantId];
                const isPending = updateTenantSettingMutation.isPending && updateTenantSettingMutation.variables?.tenantId === tenant.tenantId;
                if (!draft) {
                  return null;
                }

                return (
                  <tr key={tenant.tenantId}>
                    <td className="px-2 py-2 align-top">
                      <div className="font-medium">{tenant.tenantName}</div>
                      <div className="text-[11px] text-gray-500">Tier {tenant.tier ?? 'Unknown'} · Entitlement {tenant.entitlementMonthlyCredits ?? 0}</div>
                    </td>
                    <td className="px-2 py-2 align-top"><input aria-label={`${tenant.tenantName} customer cap usd`} type="number" min={0} step="0.01" value={draft.monthlyCostCapUsd} onChange={(e) => updateTenantDraft(tenant.tenantId, { monthlyCostCapUsd: e.target.value })} className="w-24 rounded border border-gray-300 px-1 py-1 dark:border-gray-600 dark:bg-gray-900" /></td>
                    <td className="px-2 py-2 align-top"><input aria-label={`${tenant.tenantName} entitlement credits override`} type="number" min={0} step={1} value={draft.monthlyCreditsEntitlementOverride} onChange={(e) => updateTenantDraft(tenant.tenantId, { monthlyCreditsEntitlementOverride: e.target.value })} className="w-20 rounded border border-gray-300 px-1 py-1 dark:border-gray-600 dark:bg-gray-900" /></td>
                    <td className="px-2 py-2 align-top"><input aria-label={`${tenant.tenantName} operating credits limit`} type="number" min={0} step={1} value={draft.monthlyCreditsOperatingLimit} onChange={(e) => updateTenantDraft(tenant.tenantId, { monthlyCreditsOperatingLimit: e.target.value })} className="w-20 rounded border border-gray-300 px-1 py-1 dark:border-gray-600 dark:bg-gray-900" /></td>
                    <td className="px-2 py-2 align-top"><input aria-label={`${tenant.tenantName} entitlement provider cap override usd`} type="number" min={0} step="0.01" value={draft.monthlyProviderCostCapUsdOverride} onChange={(e) => updateTenantDraft(tenant.tenantId, { monthlyProviderCostCapUsdOverride: e.target.value })} className="w-20 rounded border border-gray-300 px-1 py-1 dark:border-gray-600 dark:bg-gray-900" /></td>
                    <td className="px-2 py-2 align-top"><input aria-label={`${tenant.tenantName} operating provider cap limit usd`} type="number" min={0} step="0.01" value={draft.monthlyProviderCostCapOperatingLimitUsd} onChange={(e) => updateTenantDraft(tenant.tenantId, { monthlyProviderCostCapOperatingLimitUsd: e.target.value })} className="w-20 rounded border border-gray-300 px-1 py-1 dark:border-gray-600 dark:bg-gray-900" /></td>
                    <td className="px-2 py-2 align-top"><input aria-label={`${tenant.tenantName} override reason`} type="text" value={draft.overrideReason} onChange={(e) => updateTenantDraft(tenant.tenantId, { overrideReason: e.target.value })} placeholder="Required for override/reset" className="w-36 rounded border border-gray-300 px-1 py-1 dark:border-gray-600 dark:bg-gray-900" /></td>
                    <td className="px-2 py-2 text-right align-top">
                      <div className="flex justify-end gap-1">
                        <button type="button" className="rounded bg-gray-700 px-2 py-1 text-[11px] text-white hover:bg-gray-800 disabled:opacity-60" disabled={isPending} onClick={() => saveTenantCostCap(tenant.tenantId)}>Save Cap</button>
                        <button type="button" className="rounded bg-primary-700 px-2 py-1 text-[11px] text-white hover:bg-primary-800 disabled:opacity-60" disabled={isPending} onClick={() => saveTenantOverrides(tenant.tenantId, false)}>Save Overrides</button>
                        <button type="button" className="rounded bg-red-700 px-2 py-1 text-[11px] text-white hover:bg-red-800 disabled:opacity-60" disabled={isPending} onClick={() => saveTenantOverrides(tenant.tenantId, true)}>Reset Overrides</button>
                      </div>
                      {tenantErrors[tenant.tenantId] ? <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{tenantErrors[tenant.tenantId]}</p> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {updateTenantSettingMutation.isError ? <p className="mt-2 text-xs text-red-600 dark:text-red-400">{getErrorMessage(updateTenantSettingMutation.error)}</p> : null}
      </section>
    </div>
  );
}
